/**
 * Where the office telephone is pointed, and the one call that changes it.
 *
 * Lifted out of `CallDivert.tsx` when the rail learned to fold. The switch now
 * lives inside a section that can be shut, and "every incoming call is going to
 * somebody's mobile" is not a fact that may disappear because a heading was
 * collapsed - so the rail reads this too, to put a marker on the closed
 * heading. One hook rather than two fetches: this is a settings row, and asking
 * for it twice on every visit to the call log to draw the same answer in two
 * places is the kind of thing nobody notices until the row is behind a rate
 * limit.
 */
import { useEffect, useState } from "react";
import { useI18n } from "../../../i18n/I18nProvider";
import { getCallDivert, setCallDivert, type CallDivertState } from "../../../lib/callDivert";
import { useToast } from "../../ui";

/** The server's refusals, each with its own sentence. */
const REFUSALS: Record<string, string> = {
  invalid_number: "calls.forward.err.number",
  no_target: "calls.forward.err.noTarget",
  self_target: "calls.forward.err.self",
  invalid_until: "calls.forward.err.until",
};

export interface CallDivertApi {
  /** Null until the settings arrive, and after a request that failed. */
  state: CallDivertState | null;
  busy: boolean;
  /** The number being edited in the panel, which is not yet the saved one. */
  to: string;
  setTo: (value: string) => void;
  until: string;
  setUntil: (value: string) => void;
  /** Turn forwarding on or off, and save the number and expiry with it. */
  write: (on: boolean, number: string, moment: string) => Promise<void>;
}

/**
 * @param allowed This operator may work the telephone at all. False makes the
 *   whole thing inert rather than fetching a settings row the server would
 *   refuse them - the switch is not drawn for them either way.
 */
export function useCallDivert(allowed: boolean): CallDivertApi {
  const { t } = useI18n();
  const toast = useToast();
  const [state, setState] = useState<CallDivertState | null>(null);
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState("");
  const [until, setUntil] = useState("");

  useEffect(() => {
    if (!allowed) return;
    getCallDivert()
      .then((r) => {
        setState(r.divert);
        setTo(r.divert.to);
        setUntil(r.divert.until);
      })
      // Silently: the rail is not the place to report that one of its optional
      // sections could not be read, and the section simply does not draw.
      .catch(() => {});
  }, [allowed]);

  async function write(on: boolean, number: string, moment: string) {
    setBusy(true);
    try {
      const r = await setCallDivert({ enabled: on, to: number.trim(), until: on ? moment : "" });
      setState(r.divert);
      setTo(r.divert.to);
      setUntil(r.divert.until);
      toast(t(on ? "calls.forward.toastOn" : "calls.forward.toastOff"));
    } catch (err) {
      const code = (err as { body?: { error?: string } })?.body?.error ?? "";
      toast(t(REFUSALS[code] ?? "common.error"));
    } finally {
      setBusy(false);
    }
  }

  return { state, busy, to, setTo, until, setUntil, write };
}
